import {
  GetGrowthWheelByRoleIdDto,
  GetGrowthWheelByUserIdDto,
  GrowthWheel,
  GrowthWheelSummary,
  CreateGrowthWheelForGuildDto,
  UpdateCompetencyEvaluationDto,
  UpdateCompetencyEvaluationPayload,
  GrowthWheelEditorStructure,
  GrowthWheelVersionMeta,
  GrowthWheelAssessmentResponse,
  GrowthWheelAssessmentComparisonResponse,
  GetGrowthWheelAssessmentComparisonDto,
  CompleteGrowthWheelAssessmentDto,
  CompleteGrowthWheelAssessmentResponse,
  StartGrowthWheelAssessmentDto,
  StartGrowthWheelAssessmentResponse,
  GrowthWheelAssessmentSession,
} from '../types/GrowthWheel'
import { timeoffApi } from './timeoff.api'

async function cacheEditorStructureFromMutation(
  arg: { growthWheelId: number },
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => void
    queryFulfilled: Promise<{ data: GrowthWheelEditorStructure }>
  },
) {
  try {
    const { data } = await queryFulfilled
    const update = timeoffApi.util
      .updateQueryData as unknown as (
      endpoint: string,
      arg: { growthWheelId: number },
      updateFrom: (d: GrowthWheelEditorStructure) => GrowthWheelEditorStructure,
    ) => { type: string }
    dispatch(update('getGrowthWheelEditorStructure', { growthWheelId: arg.growthWheelId }, () => data))
  } catch (error) {
    console.error('Error caching editor structure from mutation:', error)
  }
}

export interface CompetencyEvaluationResponse {
  id: number
  selfLevel: number | null
  selfNote: string | null
  leaderLevel: number | null
  leaderNote: string | null
  focus: boolean
}

export interface GrowthWheelUserForReview {
  id: number
  firstName: string
  lastName: string
  email: string
  area: string
  isGuildMaster: boolean
  evaluationsDone: number
  totalEvaluations: number
  guildId: number
}

const growthWheelEndpoints = timeoffApi.injectEndpoints({
  endpoints: build => ({
    getAllGrowthWheels: build.query<GrowthWheelSummary[], void>({
      query: () => `/growth-wheel`,
      providesTags: ['GrowthWheelData'],
    }),
    getGrowthWheelEditorSummaries: build.query<GrowthWheelSummary[], void>({
      query: () => `/growth-wheel/editor/summaries`,
      providesTags: ['GrowthWheelData', 'GrowthWheelEditorSummaries'],
    }),
    getGrowthWheelByRoleId: build.query<GrowthWheel, GetGrowthWheelByRoleIdDto>({
      query: query =>
        `/growth-wheel/role/${query.roleId}`,
      providesTags: ['GrowthWheelData'],
    }),
    getGrowthWheelWithEvaluationsByUserId: build.query<
      GrowthWheelAssessmentResponse,
      GetGrowthWheelByUserIdDto
    >({
      query: query => {
        const params = query.assessmentSessionId
          ? `?assessmentSessionId=${query.assessmentSessionId}`
          : ''
        return `/growth-wheel/user/${query.userId}/competency-evaluations${params}`
      },
      providesTags: ['GrowthWheelData'],
    }),
    getGrowthWheelAssessmentSessionsByUserId: build.query<
      GrowthWheelAssessmentSession[],
      { userId: number }
    >({
      query: query => `/growth-wheel/user/${query.userId}/assessment-sessions`,
      providesTags: ['GrowthWheelData'],
    }),
    getGrowthWheelAssessmentComparison: build.query<
      GrowthWheelAssessmentComparisonResponse,
      GetGrowthWheelAssessmentComparisonDto
    >({
      query: query =>
        `/growth-wheel/user/${
          query.userId
        }/assessment-comparison?assessmentSessionIds=${query.assessmentSessionIds.join(
          ',',
        )}&scoreField=${query.scoreField}`,
      providesTags: ['GrowthWheelData'],
    }),
    completeGrowthWheelAssessment: build.mutation<
      CompleteGrowthWheelAssessmentResponse,
      CompleteGrowthWheelAssessmentDto
    >({
      query: ({ growthWheelId, userId }) => ({
        url: `/growth-wheel/${growthWheelId}/users/${userId}/complete-assessment`,
        method: 'POST',
      }),
      invalidatesTags: ['GrowthWheelData'],
    }),
    startGrowthWheelAssessment: build.mutation<
      StartGrowthWheelAssessmentResponse,
      StartGrowthWheelAssessmentDto
    >({
      query: ({ growthWheelId, userId, name }) => ({
        url: `/growth-wheel/${growthWheelId}/users/${userId}/start-assessment`,
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: ['GrowthWheelData'],
    }),
    getGrowthWheelUsersByGrowthWheelReviewerId: build.query<GrowthWheelUserForReview[], { reviewerId: number }>({
      query: query => `/growth-wheel/reviewer/${query.reviewerId}/users`,
      providesTags: ['GrowthWheelData'],
    }),
    updateCompetencyEvaluation: build.mutation<
      CompetencyEvaluationResponse,
      UpdateCompetencyEvaluationDto
    >({
      query: ({
        evaluationId,
        userId,
        subCompetencyId,
        assessmentSessionId,
        payload,
      }) => ({
        url: `/growth-wheel/competency-evaluation/${evaluationId}`,
        method: 'PATCH',
        body: {
          userId,
          subCompetencyId,
          assessmentSessionId,
          ...payload,
        },
      }),
      invalidatesTags: ['GrowthWheelData'],
    }),
    createGrowthWheelForGuild: build.mutation<
      GrowthWheelSummary,
      CreateGrowthWheelForGuildDto
    >({
      query: ({ roleId }) => ({
        url: `/growth-wheel/editor/guild`,
        method: 'POST',
        body: { roleId },
      }),
      invalidatesTags: ['GrowthWheelData', 'GrowthWheelEditorSummaries'],
    }),
    getGrowthWheelEditorStructure: build.query<
      GrowthWheelEditorStructure,
      { growthWheelId: number }
    >({
      query: ({ growthWheelId }) =>
        `/growth-wheel/editor/${growthWheelId}/structure`,
      providesTags: (_r, _e, { growthWheelId }) => [
        { type: 'GrowthWheelEditor', id: growthWheelId },
      ],
    }),
    getGrowthWheelVersions: build.query<
      GrowthWheelVersionMeta[],
      { growthWheelId: number }
    >({
      query: ({ growthWheelId }) =>
        `/growth-wheel/editor/${growthWheelId}/versions`,
      providesTags: (_r, _e, { growthWheelId }) => [
        { type: 'GrowthWheelEditorVersions', id: growthWheelId },
      ],
    }),
    createGrowthWheelVersion: build.mutation<
      GrowthWheelVersionMeta,
      { growthWheelId: number }
    >({
      query: ({ growthWheelId }) => ({
        url: `/growth-wheel/editor/${growthWheelId}/versions`,
        method: 'POST',
      }),
      invalidatesTags: [
        'GrowthWheelData',
        'GrowthWheelEditorSummaries',
        'GrowthWheelEditorVersions',
      ],
    }),
    activateGrowthWheelVersion: build.mutation<
      GrowthWheelVersionMeta,
      { growthWheelId: number }
    >({
      query: ({ growthWheelId }) => ({
        url: `/growth-wheel/editor/${growthWheelId}/versions/activate`,
        method: 'POST',
      }),
      invalidatesTags: [
        'GrowthWheelData',
        'GrowthWheelEditorSummaries',
        'GrowthWheelEditorVersions',
        { type: 'GrowthWheelEditor' },
      ],
    }),
    deleteGrowthWheelVersion: build.mutation<
      { deletedGrowthWheelId: number; roleId: number },
      { growthWheelId: number }
    >({
      query: ({ growthWheelId }) => ({
        url: `/growth-wheel/editor/${growthWheelId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        'GrowthWheelData',
        'GrowthWheelEditorSummaries',
        'GrowthWheelEditorVersions',
      ],
    }),
    createEditorCompetency: build.mutation<
      GrowthWheelEditorStructure,
      {
        growthWheelId: number
        body: { name: string; description?: string }
      }
    >({
      query: ({ growthWheelId, body }) => ({
        url: `/growth-wheel/editor/${growthWheelId}/competencies`,
        method: 'POST',
        body,
      }),
      async onQueryStarted(arg, api) {
        await cacheEditorStructureFromMutation(arg, api)
      },
      invalidatesTags: ['GrowthWheelData', 'GrowthWheelEditorSummaries'],
    }),
    updateEditorCompetency: build.mutation<
      GrowthWheelEditorStructure,
      {
        growthWheelId: number
        competencyId: number
        body: { name?: string; description?: string }
      }
    >({
      query: ({ growthWheelId, competencyId, body }) => ({
        url: `/growth-wheel/editor/${growthWheelId}/competencies/${competencyId}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(arg, api) {
        await cacheEditorStructureFromMutation(arg, api)
      },
      invalidatesTags: ['GrowthWheelData', 'GrowthWheelEditorSummaries'],
    }),
    deleteEditorCompetency: build.mutation<
      GrowthWheelEditorStructure,
      { growthWheelId: number; competencyId: number }
    >({
      query: ({ growthWheelId, competencyId }) => ({
        url: `/growth-wheel/editor/${growthWheelId}/competencies/${competencyId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, api) {
        await cacheEditorStructureFromMutation(arg, api)
      },
      invalidatesTags: ['GrowthWheelData', 'GrowthWheelEditorSummaries'],
    }),
    reorderEditorCompetenciesOnWheel: build.mutation<
      GrowthWheelEditorStructure,
      { growthWheelId: number; orderedCompetencyIds: number[] }
    >({
      query: ({ growthWheelId, orderedCompetencyIds }) => ({
        url: `/growth-wheel/editor/${growthWheelId}/competencies/reorder`,
        method: 'PATCH',
        body: { orderedCompetencyIds },
      }),
      async onQueryStarted(arg, api) {
        await cacheEditorStructureFromMutation(arg, api)
      },
      invalidatesTags: ['GrowthWheelData', 'GrowthWheelEditorSummaries'],
    }),
    createEditorSubCompetency: build.mutation<
      GrowthWheelEditorStructure,
      {
        growthWheelId: number
        competencyId: number
        body: { name: string; description?: string }
      }
    >({
      query: ({ growthWheelId, competencyId, body }) => ({
        url: `/growth-wheel/editor/${growthWheelId}/competencies/${competencyId}/sub-competencies`,
        method: 'POST',
        body,
      }),
      async onQueryStarted(arg, api) {
        await cacheEditorStructureFromMutation(arg, api)
      },
      invalidatesTags: ['GrowthWheelData', 'GrowthWheelEditorSummaries'],
    }),
    updateEditorSubCompetency: build.mutation<
      GrowthWheelEditorStructure,
      {
        growthWheelId: number
        subCompetencyId: number
        body: {
          name?: string
          description?: string
          levelDescriptions?: Record<number, string>
        }
      }
    >({
      query: ({ growthWheelId, subCompetencyId, body }) => ({
        url: `/growth-wheel/editor/${growthWheelId}/sub-competencies/${subCompetencyId}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(arg, api) {
        await cacheEditorStructureFromMutation(arg, api)
      },
      invalidatesTags: ['GrowthWheelData', 'GrowthWheelEditorSummaries'],
    }),
    deleteEditorSubCompetency: build.mutation<
      GrowthWheelEditorStructure,
      { growthWheelId: number; subCompetencyId: number }
    >({
      query: ({ growthWheelId, subCompetencyId }) => ({
        url: `/growth-wheel/editor/${growthWheelId}/sub-competencies/${subCompetencyId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, api) {
        await cacheEditorStructureFromMutation(arg, api)
      },
      invalidatesTags: ['GrowthWheelData', 'GrowthWheelEditorSummaries'],
    }),
    reorderEditorSubCompetencies: build.mutation<
      GrowthWheelEditorStructure,
      {
        growthWheelId: number
        parentCompetencyId: number
        orderedSubCompetencyIds: number[]
      }
    >({
      query: ({ growthWheelId, parentCompetencyId, orderedSubCompetencyIds }) => ({
        url: `/growth-wheel/editor/${growthWheelId}/competencies/${parentCompetencyId}/sub-competencies/reorder`,
        method: 'PATCH',
        body: { orderedSubCompetencyIds },
      }),
      async onQueryStarted(arg, api) {
        await cacheEditorStructureFromMutation(arg, api)
      },
      invalidatesTags: ['GrowthWheelData', 'GrowthWheelEditorSummaries'],
    }),
  }),
})

export const {
  useGetAllGrowthWheelsQuery,
  useGetGrowthWheelEditorSummariesQuery,
  useGetGrowthWheelByRoleIdQuery,
  useGetGrowthWheelWithEvaluationsByUserIdQuery,
  useGetGrowthWheelAssessmentComparisonQuery,
  useGetGrowthWheelAssessmentSessionsByUserIdQuery,
  useCompleteGrowthWheelAssessmentMutation,
  useStartGrowthWheelAssessmentMutation,
  useGetGrowthWheelUsersByGrowthWheelReviewerIdQuery,
  useUpdateCompetencyEvaluationMutation,
  useCreateGrowthWheelForGuildMutation,
  useGetGrowthWheelEditorStructureQuery,
  useGetGrowthWheelVersionsQuery,
  useCreateGrowthWheelVersionMutation,
  useActivateGrowthWheelVersionMutation,
  useDeleteGrowthWheelVersionMutation,
  useCreateEditorCompetencyMutation,
  useUpdateEditorCompetencyMutation,
  useDeleteEditorCompetencyMutation,
  useReorderEditorCompetenciesOnWheelMutation,
  useCreateEditorSubCompetencyMutation,
  useUpdateEditorSubCompetencyMutation,
  useDeleteEditorSubCompetencyMutation,
  useReorderEditorSubCompetenciesMutation,
} = growthWheelEndpoints

export type { UpdateCompetencyEvaluationPayload }
